import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  FaBed,
  FaCar,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaPlane,
  FaShieldAlt,
  FaStar,
  FaUsers,
  FaWifi
} from 'react-icons/fa'

import api from 'services/api'
import CityModal from 'components/cityModal'
import { ModelCity } from 'models/ModelCity'
import { ModelItineraries } from 'models/ModelItineraries'
import { getItineraryPublicPath } from 'utils/itineraryUrl'
import { findDestinationCatalogState } from 'utils/destinationCatalog'
import MapPage from '../Map'
import {
  AffiliateCard,
  AffiliateGrid,
  Avatar,
  AvatarMore,
  AvatarStack,
  CommunityActions,
  CommunityCard,
  CommunityCopy,
  CommunityStats,
  Container,
  CtaBanner,
  CtaButton,
  CtaCopy,
  DestinationCard,
  DestinationCount,
  DestinationGrid,
  DestinationName,
  FeaturedGrid,
  ItineraryCard,
  ItineraryCover,
  ItineraryInfo,
  ItineraryMeta,
  ItineraryTitle,
  OutlineButton,
  PrimaryButton,
  RatingLine,
  Section,
  SectionHeader,
  SectionIntro,
  SectionLink,
  Stat,
  StatDivider
} from './style'

type HomeUser = {
  id: string
  firstname: string
  lastname: string
  profilePicture?: string
  itineraries?: ModelItineraries[]
}

type Destination = {
  key: string
  name: string
  count: number
  image: string
  city: ModelCity
  slug?: string
}

const FALLBACK_COVER = '/images/itinerary-banner.png'
const FALLBACK_AVATAR = '/images/redsternagray-logo.png'

const normalizeCityName = (name = '') =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const Home = () => {
  const history = useHistory()
  const [itineraries, setItineraries] = useState<ModelItineraries[]>([])
  const [users, setUsers] = useState<HomeUser[]>([])
  const [destinationCatalog, setDestinationCatalog] = useState<any[]>([])
  const [destinationStates, setDestinationStates] = useState<any[]>([])
  const [selectedDestination, setSelectedDestination] =
    useState<ModelCity | null>(null)
  const [isDestinationOpen, setIsDestinationOpen] = useState(false)

  const isMobile = window.innerWidth < 800
  const authenticated = Boolean(window.localStorage.getItem('token'))

  useEffect(() => {
    const loadHomeData = async () => {
      const [
        itineraryResult,
        userResult,
        destinationCatalogResult,
        destinationStateResult
      ] = await Promise.allSettled([
        api.get('/itineraries'),
        api.get('/users/all'),
        api.get('/itineraries/destination-catalog'),
        api.get('/cities/catalog-state')
      ])

      if (itineraryResult.status === 'fulfilled') {
        setItineraries(itineraryResult.value.data ?? [])
      }

      if (userResult.status === 'fulfilled') {
        setUsers(userResult.value.data ?? [])
      }

      if (destinationCatalogResult.status === 'fulfilled') {
        setDestinationCatalog(destinationCatalogResult.value.data ?? [])
      }

      if (destinationStateResult.status === 'fulfilled') {
        setDestinationStates(destinationStateResult.value.data ?? [])
      }
    }

    loadHomeData()
  }, [])

  const featuredItineraries = useMemo(() => {
    return [...itineraries]
      .sort((a, b) => {
        const aRates = a.rates ?? []
        const bRates = b.rates ?? []
        const aAvg = aRates.length
          ? aRates.reduce((sum, item) => sum + Number(item.rate || 0), 0) /
            aRates.length
          : 0
        const bAvg = bRates.length
          ? bRates.reduce((sum, item) => sum + Number(item.rate || 0), 0) /
            bRates.length
          : 0

        return bRates.length - aRates.length || bAvg - aAvg
      })
      .slice(0, 4)
  }, [itineraries])

  const destinations = useMemo<Destination[]>(() => {
    const destinationMap = new Map<string, Destination>()

    itineraries.forEach((itinerary) => {
      ;(itinerary.cities ?? []).forEach((city) => {
        const name = city?.name?.trim()
        const latitude = Number(city?.latitude)
        const longitude = Number(city?.longitude)

        if (
          !name ||
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)
        ) {
          return
        }

        const key = `${normalizeCityName(name)}|${latitude.toFixed(
          3
        )}|${longitude.toFixed(3)}`
        const existing = destinationMap.get(key)
        const cityImage = city.images?.find((image) => image?.file)?.file
        const destinationState = findDestinationCatalogState(
          destinationStates,
          city
        )

        if (existing) {
          existing.count += 1

          if (cityImage) {
            existing.image = cityImage
          } else if (
            existing.image === FALLBACK_COVER &&
            itinerary.coverImage
          ) {
            existing.image = itinerary.coverImage
          }

          if (!existing.slug && destinationState?.slug) {
            existing.slug = destinationState.slug
          }
          return
        }

        destinationMap.set(key, {
          key,
          name,
          count: 1,
          image: cityImage || itinerary.coverImage || FALLBACK_COVER,
          city,
          slug: destinationState?.slug || undefined
        })
      })
    })

    return Array.from(destinationMap.values())
      .map((destination) => ({
        destination,
        score: Math.pow(Math.random(), 1 / Math.max(destination.count, 1))
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map(({ destination }) => destination)
  }, [destinationStates, itineraries])

  const uniqueDestinations = useMemo(() => {
    if (destinationCatalog.length > 0) {
      const hiddenKeys = new Set(
        destinationStates
          .filter((state) => state?.status === 'hidden')
          .map((state) => state?.destinationKey)
          .filter(Boolean)
      )

      return destinationCatalog.filter(
        (destination) => !hiddenKeys.has(destination?.key)
      ).length
    }

    const cities = new Set<string>()
    itineraries.forEach((itinerary) =>
      (itinerary.cities ?? []).forEach((city) => {
        if (city?.name) cities.add(city.name.trim().toLocaleLowerCase('pt-BR'))
      })
    )
    return cities.size
  }, [destinationCatalog, destinationStates, itineraries])

  const communityAvatars = users
    .filter((user) => user.profilePicture)
    .slice(0, 5)

  const affiliateItems = [
    {
      title: 'Passagens',
      text: 'Compare preços e encontre boas oportunidades',
      icon: <FaPlane />
    },
    {
      title: 'Hospedagem',
      text: 'Hotéis e acomodações para todos os estilos',
      icon: <FaBed />
    },
    {
      title: 'Aluguel de carro',
      text: 'Liberdade para explorar no seu ritmo',
      icon: <FaCar />
    },
    {
      title: 'Seguro viagem',
      text: 'Mais tranquilidade para a sua aventura',
      icon: <FaShieldAlt />
    },
    {
      title: 'Chip / Internet',
      text: 'Conectividade para continuar viajando',
      icon: <FaWifi />
    }
  ]

  return (
    <Container>
      <MapPage height={isMobile ? 350 : 500} />

      <Section>
        <SectionHeader>
          <SectionIntro>
            <h1>
              Inspire sua <strong>próxima viagem</strong>
            </h1>
            <p>
              Explore experiências da comunidade e roteiros selecionados pela
              RedSterna.
            </p>
          </SectionIntro>
          <SectionLink onClick={() => history.push('/roteiros')}>
            Ver todos os roteiros →
          </SectionLink>
        </SectionHeader>

        <FeaturedGrid>
          {featuredItineraries.map((itinerary) => {
            const rates = itinerary.rates ?? []
            const average = rates.length
              ? rates.reduce((sum, item) => sum + Number(item.rate || 0), 0) /
                rates.length
              : 0

            return (
              <ItineraryCard
                key={itinerary.id}
                onClick={() => history.push(getItineraryPublicPath(itinerary))}
              >
                <ItineraryCover
                  src={itinerary.coverImage || FALLBACK_COVER}
                  alt={itinerary.title}
                />
                <ItineraryInfo>
                  <ItineraryTitle>{itinerary.title}</ItineraryTitle>
                  <RatingLine>
                    {rates.length > 0 ? (
                      <>
                        <FaStar />
                        <strong>{average.toFixed(1).replace('.', ',')}</strong>
                        <span>({rates.length})</span>
                      </>
                    ) : (
                      <span>Sem avaliação</span>
                    )}
                  </RatingLine>
                  <ItineraryMeta>
                    <span>
                      <FaCalendarAlt /> {itinerary.days} dias
                    </span>
                    <span>
                      <FaMapMarkerAlt /> {itinerary.cities?.length ?? 0}{' '}
                      destinos
                    </span>
                  </ItineraryMeta>
                </ItineraryInfo>
              </ItineraryCard>
            )
          })}
        </FeaturedGrid>
      </Section>

      <Section>
        <CommunityCard>
          <CommunityCopy>
            <h2>
              Faça parte da comunidade <strong>RedSterna</strong>
            </h2>
            <p>
              Compartilhe roteiros, inspire viajantes e descubra novas
              experiências.
            </p>
            <AvatarStack>
              {communityAvatars.map((user) => (
                <Avatar
                  key={user.id}
                  src={user.profilePicture || FALLBACK_AVATAR}
                  alt={user.firstname}
                />
              ))}
              {users.length > communityAvatars.length && (
                <AvatarMore>
                  +{users.length - communityAvatars.length}
                </AvatarMore>
              )}
            </AvatarStack>
          </CommunityCopy>

          <CommunityStats>
            <Stat>
              <FaUsers />
              <div>
                <strong>{users.length}</strong>
                <span>viajantes</span>
              </div>
            </Stat>
            <StatDivider />
            <Stat>
              <FaMapMarkerAlt />
              <div>
                <strong>{itineraries.length}</strong>
                <span>roteiros</span>
              </div>
            </Stat>
            <StatDivider />
            <Stat>
              <FaMapMarkerAlt />
              <div>
                <strong>{uniqueDestinations}</strong>
                <span>destinos</span>
              </div>
            </Stat>
          </CommunityStats>

          <CommunityActions>
            <span>Viagens ficam ainda melhores quando são compartilhadas.</span>
            <div>
              {!authenticated && (
                <PrimaryButton onClick={() => history.push('/cadastre-se')}>
                  Criar meu perfil
                </PrimaryButton>
              )}
              <OutlineButton onClick={() => history.push('/buscar-usuarios')}>
                Conhecer viajantes
              </OutlineButton>
            </div>
          </CommunityActions>
        </CommunityCard>
      </Section>

      <Section>
        <SectionHeader>
          <SectionIntro>
            <h2>
              Explore <strong>destinos</strong>
            </h2>
            <p>Descubra lugares através dos roteiros da nossa comunidade.</p>
          </SectionIntro>
          <SectionLink onClick={() => history.push('/destinos')}>
            Ver todos os destinos →
          </SectionLink>
        </SectionHeader>

        <DestinationGrid>
          {destinations.map((destination) => (
            <DestinationCard
              key={`${destination.name}-${destination.city.latitude}-${destination.city.longitude}`}
              $image={destination.image}
              onClick={() => {
                if (destination.slug) {
                  history.push(`/destinos/${destination.slug}`)
                  return
                }

                setSelectedDestination(destination.city)
                setIsDestinationOpen(true)
              }}
            >
              <DestinationName>{destination.name}</DestinationName>
              <DestinationCount>
                {destination.count}{' '}
                {destination.count === 1 ? 'roteiro' : 'roteiros'}
              </DestinationCount>
            </DestinationCard>
          ))}
        </DestinationGrid>
      </Section>

      <Section>
        <SectionHeader>
          <SectionIntro>
            <h2>
              Planeje melhor <strong>sua viagem</strong>
            </h2>
            <p>Encontre em um só lugar o que precisa para viajar melhor.</p>
          </SectionIntro>
        </SectionHeader>

        <AffiliateGrid>
          {affiliateItems.map((item) => (
            <AffiliateCard
              key={item.title}
              onClick={() => history.push('/dicas-de-viagem')}
            >
              <div>{item.icon}</div>
              <span>
                <strong>{item.title}</strong>
                <small>{item.text}</small>
              </span>
              <b>›</b>
            </AffiliateCard>
          ))}
        </AffiliateGrid>
      </Section>

      <Section>
        <CtaBanner>
          <CtaCopy>
            <h2>
              Sua <strong>próxima história</strong> começa aqui
            </h2>
            <p>
              Planeje, registre e compartilhe suas viagens com a comunidade
              RedSterna.
            </p>
          </CtaCopy>
          <CtaButton
            onClick={() =>
              history.push(authenticated ? '/criar-roteiro' : '/cadastre-se')
            }
          >
            {authenticated ? '+ Criar roteiro' : 'Criar meu perfil'}
          </CtaButton>
        </CtaBanner>
      </Section>

      {selectedDestination && (
        <CityModal
          isOpen={isDestinationOpen}
          city={selectedDestination}
          closeModal={() => setIsDestinationOpen(false)}
        />
      )}
    </Container>
  )
}

export default Home
